import { Component, OnInit, inject, signal } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ParticipantService, SubjectParticipant } from '@galaxy/conversation/core';
import { TranslateService } from '@ngx-translate/core';
import { ConversationChannel, GlobalParticipantType, Participant, PlatformLocation } from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, ListAuxiliaryDataResponse } from '@vendasta/iamv2';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { SalesActivityApiService } from '../../sales/sales-activity/sales-activity-api.service';
import { AssignedSalespersonService } from '../assigned-salesperson.service';
import { toSignal } from '@angular/core/rxjs-interop';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InboxAiSupportBannerComponent } from '@galaxy/conversation/ui';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { MatDividerModule } from '@angular/material/divider';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InboxButtonModule } from '@galaxy/conversation/button';
import { FormatPhoneNumberPipe } from '../../common/pipes/format-phone-number.pipe';
import { Router } from '@angular/router';
import { SYSTEM_ASSISTANT_ID } from '@galaxy/ai-assistant/core';
import { HelpSideDrawerTemplateRefService } from '../../help-center/help-side-drawer/help-side-drawer-template-ref.service';

const DEFAULT_IMAGE_URL =
  'https://lh3.googleusercontent.com/70DGcv8fyAzy1n_cWh8GyVbsCIWzPGZMdBFiwJa8Ot-Ss5Rf_oZY8hKOG2wAttY4_iuU5TlO9NLlDrBGuEJBj6DHwRnGd2CJ32J2Im3c1g';

@Component({
  templateUrl: './assigned-salesperson-dialog.component.html',
  styleUrls: ['./assigned-salesperson-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslateModule,
    MatDividerModule,
    GalaxyLoadingSpinnerModule,
    InboxButtonModule,
    MatDialogModule,
    FormatPhoneNumberPipe,
    InboxAiSupportBannerComponent,
  ],
})
export class AssignedSalespersonDialogComponent implements OnInit {
  name: string;
  title: string;
  tel: string;
  email: string;
  imgUrl: string;
  countryCode: string;
  private readonly salespersonId: string;
  private readonly businessId: string;

  sending = false;
  buttonEnabled = false;
  message = '';

  bookMeetingUrl$: Observable<string>;
  awayMessage$: Observable<string>;

  private dialogRef = inject<MatDialogRef<AssignedSalespersonDialogComponent>>(MatDialogRef);
  private data = inject(MAT_DIALOG_DATA);
  private salesActivityService = inject(SalesActivityApiService);
  private assignedSalespersonService = inject(AssignedSalespersonService);
  private snackbarService = inject(SnackbarService);
  private translateService = inject(TranslateService);
  private participantService = inject(ParticipantService);
  private readonly userService = inject(IAMService);
  private readonly router = inject(Router);
  private readonly partnerId$ = this.assignedSalespersonService.partnerId$;
  currentIAMParticipant$ = this.participantService.buildIAMUserParticipant() as Observable<Participant>;

  readonly platformLocation = PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER;
  readonly assistantId = SYSTEM_ASSISTANT_ID;
  readonly contactAIChannel = ConversationChannel.CONVERSATION_CHANNEL_OPENAI;
  readonly salesConversationChannel = ConversationChannel.CONVERSATION_CHANNEL_INTERNAL;
  protected readonly isLoading = signal(true);
  private readonly helpSideDrawerTemplateRefService = inject(HelpSideDrawerTemplateRefService);
  protected readonly canAccessInbox = toSignal(this.assignedSalespersonService.canAccessInbox$, {
    initialValue: false,
  });

  protected readonly canAccessInboxAiSupport = toSignal(
    combineLatest([
      this.assignedSalespersonService.canAccessInboxAiSupport$,
      this.assignedSalespersonService.canAccessInboxAiTab$,
    ]).pipe(map(([canAccessInboxAiSupport, canAccessInboxAiTab]) => canAccessInboxAiSupport && canAccessInboxAiTab)),
    {
      initialValue: false,
    },
  );

  private readonly dataLoaded$ = combineLatest([
    this.assignedSalespersonService.canAccessInbox$,
    this.assignedSalespersonService.canAccessInboxAiSupport$,
    this.assignedSalespersonService.canAccessInboxAiTab$,
  ]);

  readonly salesContactSubjectParticipants$ = this.partnerId$.pipe(
    map((partnerId) => [
      new SubjectParticipant({
        internalParticipantId: partnerId,
        participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
      }),
      new SubjectParticipant({
        internalParticipantId: 'VMF',
        participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
      }),
    ]),
  );

  readonly aiContactSubjectParticipants = new SubjectParticipant({
    internalParticipantId: '',
    participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_OPENAI_BOT,
  });

  constructor() {
    this.name = this.data.name;
    this.title = this.data.title;
    this.imgUrl = this.data.imgUrl ? this.data.imgUrl : DEFAULT_IMAGE_URL;
    this.tel = this.data.telephone;
    this.email = this.data.email;
    this.salespersonId = this.data.salespersonId;
    this.businessId = this.data.businessId;
    this.countryCode = this.data.country;

    this.dataLoaded$.pipe(take(1)).subscribe(() => {
      this.isLoading.set(false);
    });
  }

  ngOnInit(): void {
    this.bookMeetingUrl$ = this.assignedSalespersonService.bookMeetingUrl$;
    this.awayMessage$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.userService.listUserAuxiliaryData(
          {
            partnerId: partnerId,
            objectId: this.salespersonId,
          },
          {
            cursor: '',
            pageSize: 100,
          },
        );
      }),
      map((resp: ListAuxiliaryDataResponse) => {
        let awayMessage = '';
        const objectSchema = JSON.parse(resp.jsonSchema);
        const objectData = JSON.parse(resp.jsonData);
        for (const property in objectSchema.properties) {
          if (objectSchema.properties[property].title === 'Away Message') {
            awayMessage = <string>objectData[property];
            break;
          }
        }
        return awayMessage;
      }),
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  enableSend(): void {
    this.buttonEnabled = !!this.message;
  }

  chatWithAiButtonClicked(): void {
    this.helpSideDrawerTemplateRefService.close();
    this.dialogRef.close();
    this.router.navigate(['', { outlets: { inbox: 'inbox/ai' } }]);
  }

  send(): void {
    if (this.message) {
      this.sending = true;
      this.salesActivityService
        .createContactUsInboundMessageSalesActivity(this.businessId, this.salespersonId, this.message)
        .pipe(take(1))
        .subscribe(
          () => {
            this.dialogRef.close();
            this.sending = false;
          },
          () => {
            this.sending = false;
            this.snackbarService.errorSnack(this.translateService.instant('CONTACT_SALESPERSON.ERROR'));
          },
        );
    }
  }
}
